import { useMemo } from 'react'

import { Text } from '@wartek-id/text-v1'

import {
  isRequiredTugasTambahan,
  resolvePerencanaanReviewDiff,
} from 'app/KinerjaGuruPage/utils/perencanaan'
import useToggleSdkContext from 'utils/useToogleSdk'
import { IndikatorPerencanaanPanel } from 'app/KinerjaShared/components/IndikatorPerencanaanPanel'
import { PerencanaanReviewDiff } from 'app/KinerjaShared/components/PerencanaanReviewDiff'

import type { PerencanaanItemReviewDiff } from 'app/KinerjaGuruPage/types/perencanaan'
import type { KinerjaUtamaDetail } from 'types/kinerja'

export type TugasTambahanItemProps = {
  originalIds: Array<number>
  reviewIds: Array<number>
  showReview: boolean
  contents: Array<KinerjaUtamaDetail>
  orderNumber: number
}

export function TugasTambahanItems({
  originalIds,
  reviewIds,
  showReview,
  contents,
  orderNumber,
}: TugasTambahanItemProps) {
  // Content as map for constant time lookup
  const contentsMap: Record<number, KinerjaUtamaDetail> = useMemo(() => {
    return contents.reduce((acc, content) => {
      acc[content.id] = content
      return acc
    }, {} as Record<number, KinerjaUtamaDetail>)
  }, [contents])

  const toggleValue = useToggleSdkContext()
  const isTugasTambahanRequiredEnabled =
    toggleValue?.['kinerja-tugas-tambahan-required-enabled']

  const {
    requiredItems,
    optionalItems,
  }: {
    requiredItems: Array<PerencanaanItemReviewDiff<number>>
    optionalItems: Array<PerencanaanItemReviewDiff<number>>
  } = useMemo(() => {
    let items: Array<PerencanaanItemReviewDiff<number>> = []

    if (showReview) {
      items = resolvePerencanaanReviewDiff(originalIds, reviewIds)
    } else {
      items = originalIds.map((id) => ({
        isChanged: false,
        original: id,
        review: null,
      }))
    }

    const requiredItems = []
    const optionalItems = []

    items.forEach((item) => {
      const itemId = showReview ? item.review ?? item.original : item.original

      if (
        isTugasTambahanRequiredEnabled &&
        isRequiredTugasTambahan(contentsMap[itemId]?.tag)
      ) {
        requiredItems.push(item)
        return
      }

      optionalItems.push(item)
    })

    return { requiredItems, optionalItems }
  }, [
    originalIds,
    reviewIds,
    contentsMap,
    showReview,
    isTugasTambahanRequiredEnabled,
  ])

  return (
    <>
      {requiredItems.map((item, index) => {
        const resolvedOrderNumber = `${orderNumber}.${index + 1}`
        const shouldShowReview = showReview && item.isChanged
        const resolvedText =
          contentsMap[showReview ? item.review ?? item.original : item.original]
            ?.rencanaHasilKerja

        return (
          <IndikatorPerencanaanPanel
            key={`tugas-tambahan-required-${index}`}
            variant="static"
            orderNumber={resolvedOrderNumber}
            title={resolvedText}
            upperContent={
              shouldShowReview && (
                <PerencanaanReviewDiff
                  originalLabel="Perencanaan Guru"
                  reviewLabel="Penyesuaian dari Kepala Sekolah"
                  originalContent={
                    item.original && (
                      <IndikatorPerencanaanPanel
                        variant="static"
                        orderNumber={resolvedOrderNumber}
                        title={contentsMap[item.original]?.rencanaHasilKerja}
                      />
                    )
                  }
                  reviewContent={
                    <IndikatorPerencanaanPanel
                      variant="static"
                      orderNumber={resolvedOrderNumber}
                      title={contentsMap[item.review]?.rencanaHasilKerja}
                    />
                  }
                />
              )
            }
          />
        )
      })}

      {optionalItems.map((item, index) => {
        const resolvedOrderNumber = `${orderNumber}.${
          requiredItems.length + index + 1
        }`

        const hasOriginal = !!item.original
        const shouldShowReview = showReview && item.isChanged

        return (
          <IndikatorPerencanaanPanel
            key={`tugas-tambahan-optional-${index}`}
            variant="static"
            orderNumber={resolvedOrderNumber}
            upperContent={
              shouldShowReview && (
                <PerencanaanReviewDiff
                  originalLabel="Perencanaan Guru"
                  reviewLabel="Penyesuaian dari Kepala Sekolah"
                  originalContent={
                    hasOriginal && (
                      <IndikatorPerencanaanPanel
                        variant="static"
                        orderNumber={resolvedOrderNumber}
                      >
                        <Text>
                          Terlaksananya tugas tambahan dalam mendukung
                          pembelajaran yang bermutu untuk semua berdasarkan
                          pilihan&nbsp;
                          <strong>
                            {contentsMap[item.original]?.rencanaHasilKerja}
                          </strong>
                        </Text>
                      </IndikatorPerencanaanPanel>
                    )
                  }
                  reviewContent={
                    <IndikatorPerencanaanPanel
                      variant="static"
                      orderNumber={resolvedOrderNumber}
                    >
                      <Text>
                        Terlaksananya tugas tambahan dalam mendukung
                        pembelajaran yang bermutu untuk semua berdasarkan
                        pilihan&nbsp;
                        <strong>
                          {contentsMap[item.review]?.rencanaHasilKerja}
                        </strong>
                      </Text>
                    </IndikatorPerencanaanPanel>
                  }
                />
              )
            }
          >
            <Text>
              Terlaksananya tugas tambahan dalam mendukung pembelajaran yang
              bermutu untuk semua berdasarkan pilihan&nbsp;
              <strong>
                {
                  contentsMap[
                    showReview ? item.review ?? item.original : item.original
                  ]?.rencanaHasilKerja
                }
              </strong>
            </Text>
          </IndikatorPerencanaanPanel>
        )
      })}
    </>
  )
}
